import { SceneChild } from '../types/portfolio_types'
import * as THREE from 'three'
import { useRef, useState, useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { useAtom } from 'jotai'
import { getRandomColor } from '../helpers/helperFunctions'
import {
  boundingBoxesMapAtom,
  FAST_DEVICE_DELTA,
  justPickedApplesAtom,
  onTopOfObstacleAtom,
  pickedApplesAtom,
  showHelpersAtom,
} from '../store/store'
import { getAppleData } from '../store/localstorageData'

type ButtonProps = {
  red_button: SceneChild
  button_base: SceneChild
  balls_box: SceneChild
  balls_box_lid: SceneChild
  box_apple: SceneChild
}

type BallObj = { mesh: THREE.Mesh; collider: THREE.Sphere; velocity: THREE.Vector3; name: string }

const WORLD_END_Z = 42
const BOX_LID_MIN_Y = 8.75

const Button = ({ red_button, button_base, balls_box, balls_box_lid, box_apple }: ButtonProps) => {
  // Performance tip: Do not use setState inside useFrame, use references instead as they don't trigger re-renders
  const { scene } = useThree()
  const [bbMap] = useAtom(boundingBoxesMapAtom)

  const button_ref = useRef(null)
  const box_ref = useRef(null)
  const [balls, setBalls] = useState<BallObj[]>([])
  const [onTopOfObstacle] = useAtom(onTopOfObstacleAtom)
  const [showHelpers] = useAtom(showHelpersAtom)

  const [pickedApples, setPickedApples] = useAtom(pickedApplesAtom)
  const [justPickedApples, setJustPickedApples] = useAtom(justPickedApplesAtom)

  const lid_initial_position_x = useRef<number>()
  const btn_initial_position_y = useRef<number>()

  const LID_MOVEMENT_SPEED = 5
  const OPEN_LID_X = -5
  const PRESSED_BTN_Y = 0.6

  const vector1 = useRef(new THREE.Vector3())
  const vector2 = useRef(new THREE.Vector3())
  const vector3 = useRef(new THREE.Vector3())

  const vector4 = useRef(new THREE.Vector3())
  const vector5 = useRef(new THREE.Vector3())
  const vector6 = useRef(new THREE.Vector3(0, 1, 0))

  const updateBboxes = (obj_name: string) => {
    if (!bbMap) return

    const objsToUpdate: { [key: string]: SceneChild } = {
      balls_box_lid,
      red_button,
    }

    // balls_box_lid.object is not the exact same reference used to create the bbox, so
    // we need to manually update the helpers. In Bianca this is not necessary because we
    // hold the object used to create the bbox in a ref all the time (bianca_body.current)
    bbMap[obj_name].bbox.setFromObject(objsToUpdate[obj_name].object)

    if (showHelpers) {
      const helper = scene.getObjectByName(`helper_${obj_name}`) as THREE.Box3Helper
      helper.box.copy(bbMap[obj_name].bbox)
    }
  }

  const pickApple = (apple_name: string) => {
    if (!pickedApples) return

    const apple_data = getAppleData()
    const updated_apples = { ...apple_data }

    if (updated_apples.list[apple_name] === false) {
      updated_apples.collected += 1
      updated_apples.list[apple_name] = true

      setPickedApples(updated_apples)
      setJustPickedApples({
        ...justPickedApples,
        [apple_name]: true,
      })

      setBalls(balls.filter((ball) => ball.name !== apple_name))
    }
  }

  useEffect(() => {
    if (!pickedApples) return

    const transparentMaterial = new THREE.MeshLambertMaterial({
      color: '#ecfcfd',
      opacity: 0.3,
      transparent: true,
      side: THREE.DoubleSide,
    })

    const balls_box_mesh = balls_box.object as THREE.Mesh
    const balls_box_lid_mesh = balls_box_lid.object as THREE.Mesh

    balls_box_mesh.material = transparentMaterial
    balls_box_lid_mesh.material = transparentMaterial

    const generateBalls = () => {
      if (balls.length) return balls

      const generated_balls: BallObj[] = []

      const SPHERE_RADIUS = 0.5
      const box_bbox = new THREE.Box3().setFromObject(balls_box.object)
      const balls_number = 80
      const position_offset = new THREE.Vector3()
      const balls_per_row = 4
      const balls_per_layer = 16

      for (let i = 0; i < balls_number; i++) {
        const ball_offset = 1.1
        const ball_offset_initial_z = 1.3

        const ball_position = new THREE.Vector3(box_bbox.min.x, box_bbox.min.y, box_bbox.max.z).add({
          x: 0.3,
          y: 0.6,
          z: -ball_offset_initial_z,
        })

        position_offset.x += ball_offset

        // Move the ball position to the next row (back all the way to the left with x, and forward with z)
        if (i && i % balls_per_row === 0) {
          position_offset.z -= ball_offset
          position_offset.x -= ball_offset * balls_per_row
        }

        // Move the ball position to the next layer (above the previous ones)
        // No need move back in the x as that was done above
        if (i && i % balls_per_layer === 0) {
          // go backward to the first row
          position_offset.z += balls_per_row * ball_offset
          // go up one layer
          position_offset.y += ball_offset
        }

        ball_position.add(position_offset)
        const ball_color = getRandomColor()

        const sphereGeometry = new THREE.SphereGeometry(SPHERE_RADIUS, 12, 12)
        const sphereMaterial = new THREE.MeshLambertMaterial({ color: ball_color })
        const mesh = new THREE.Mesh(sphereGeometry, sphereMaterial)
        mesh.position.copy(ball_position)

        const replace_ball_with_apple = !pickedApples.list[box_apple.object.name] && i === 34

        // No need for a helper to visualize the sphere, giving the collider the same
        // position and radius as the mesh will make the mesh itself a helper.
        // But if you want to, use a mesh with a SphereGeometry + MeshBasicMaterial with
        // any color and wireframe set to true, copy the position to the mesh and add it to
        // the scene.
        generated_balls.push({
          mesh: replace_ball_with_apple ? (box_apple.object as THREE.Mesh) : mesh,
          collider: new THREE.Sphere(ball_position, SPHERE_RADIUS),
          velocity: new THREE.Vector3(),
          name: replace_ball_with_apple ? box_apple.object.name : `ball_${i}`,
        })
      }

      return generated_balls
    }

    setBalls(generateBalls())
  }, [balls_box, balls, balls_box_lid, box_apple, pickedApples])

  // Bounciness goes from 1 to 2.
  const GRAVITY = 0.1
  const BOUNCINESS = 1.6

  function ballsCollisions() {
    for (let i = 0, length = balls.length; i < length; i++) {
      const ball1 = balls[i]

      for (let j = i + 1; j < length; j++) {
        const ball2 = balls[j]

        const d2 = ball1.collider.center.distanceToSquared(ball2.collider.center)
        const r = ball1.collider.radius + ball2.collider.radius
        const r2 = r * r

        if (d2 < r2) {
          const normal = vector1.current.subVectors(ball1.collider.center, ball2.collider.center).normalize()
          const v1 = vector2.current.copy(normal).multiplyScalar(normal.dot(ball1.velocity))
          const v2 = vector3.current.copy(normal).multiplyScalar(normal.dot(ball2.velocity))

          ball1.velocity.add(v2).sub(v1)
          ball2.velocity.add(v1).sub(v2)

          const d = (r - Math.sqrt(d2)) / 2

          ball1.collider.center.addScaledVector(normal, d)
          ball2.collider.center.addScaledVector(normal, -d)
        }
      }
    }
  }

  const updateBalls = (delta: number) => {
    if (!bbMap) return
    const fast_device = delta <= FAST_DEVICE_DELTA
    const VELOCITY_THRESHOLD = delta * 170 * (fast_device ? 1 : 3)

    for (const ball of balls) {
      ball.collider.center.addScaledVector(ball.velocity, delta)

      for (const bb_key in bbMap) {
        const { type, bbox, name } = bbMap[bb_key]
        if (type !== 'collision') continue

        const intersects = ball.collider.intersectsBox(bbox)

        if (intersects) {
          // 1) Find the closest point on the box to the sphere center
          const closestPoint = bbox.clampPoint(ball.collider.center, vector4.current)

          // 2) Compute normal from box surface to sphere center
          const normal = ball.collider.center.clone().sub(closestPoint).normalize()

          // 3) Compute penetration depth
          const distance = ball.collider.center.distanceTo(closestPoint)
          const penetrationDepth = ball.collider.radius - distance

          if (penetrationDepth > 0) {
            //4)  Reflect the velocity along the collision normal
            const velocityDot = ball.velocity.dot(normal)

            // 5) Bounce factor
            const ball_is_within_box = ball.collider.center.y > BOX_LID_MIN_Y
            const ball_low_vel = ball.velocity.lengthSq() < VELOCITY_THRESHOLD

            // Do not bounce if the ball is within the box and has a low vel. This prevents "jittery".
            const y_bounciness = ball_is_within_box && ball_low_vel ? 1 : BOUNCINESS
            const randomness = 1 + (Math.random() - 0.5) * 0.1
            ball.velocity.addScaledVector(normal, -velocityDot * y_bounciness * randomness)

            // 6) Push the ball out of the box surface
            ball.collider.center.add(normal.multiplyScalar(penetrationDepth))

            // Prevent balls from crossing the world end barrier (could happen without this line)
            if (ball.mesh.position.z > WORLD_END_Z) {
              ball.velocity.z = -2
            }

            // 7) Add an additional impact if Bianca is the one pushing it
            if (name === 'bianca') {
              ball.velocity.addScaledVector(normal, -velocityDot * 7)

              if (ball.name === box_apple.object.name) {
                pickApple(box_apple.object.name)
              }
            }
          }
        } else {
          ball.velocity.y -= GRAVITY * delta * (fast_device ? 1 : 2)
        }
      }
    }

    ballsCollisions()

    for (const ball of balls) {
      ball.mesh.position.copy(ball.collider.center)

      // ADD ROTATION
      const horizontalVelocity = ball.velocity.clone()
      horizontalVelocity.y = 0

      const speed = horizontalVelocity.length()

      // Ignore very small velocities to avoid jitter
      if (speed > 0.0001) {
        // Rotation axis is perpendicular to movement direction (right-hand rule)
        const rotationAxis = vector5.current.crossVectors(vector6.current, horizontalVelocity).normalize()

        // Angle = distance moved / radius (like real rolling)
        const angle = (speed * delta) / ball.collider.radius

        ball.mesh.rotateOnWorldAxis(rotationAxis, angle)
        vector5.current.set(0, 0, 0)
      }
    }
  }

  useFrame((_state, delta) => {
    if (!button_ref.current || !bbMap) return

    const safeDelta = Math.min(delta, 0.01)

    updateBalls(safeDelta)

    if (!lid_initial_position_x.current) {
      lid_initial_position_x.current = balls_box_lid.object.position.x
    }

    if (!btn_initial_position_y.current) {
      btn_initial_position_y.current = red_button.object.position.y
    }

    if (onTopOfObstacle === 'red_button') {
      if (balls_box_lid.object.position.x < OPEN_LID_X) {
        balls_box_lid.object.position.x += LID_MOVEMENT_SPEED * safeDelta
        updateBboxes('balls_box_lid')
      }

      if (red_button.object.position.y > PRESSED_BTN_Y) {
        red_button.object.position.y -= 2 * safeDelta
        updateBboxes('red_button')
      }
    } else {
      if (balls_box_lid.object.position.x > lid_initial_position_x.current) {
        balls_box_lid.object.position.x -= LID_MOVEMENT_SPEED * safeDelta
        updateBboxes('balls_box_lid')
      }

      if (red_button.object.position.y < btn_initial_position_y.current) {
        red_button.object.position.y += 2 * safeDelta
        updateBboxes('red_button')
      }
    }
  })

  return (
    <group>
      <group key={button_base.object.id}>{button_base.element}</group>

      <group key={red_button.object.id} ref={button_ref}>
        {red_button.element}
      </group>

      <group ref={box_ref}>{balls_box.element}</group>

      {balls.map((ball, i) => {
        return <primitive object={ball.mesh} key={`ball_${i}`} />
      })}
    </group>
  )
}

export default Button
